import { DecimalPipe } from '@angular/common';
import { OrderAttributes } from '@albatrosdeveloper/ave-models-package';
import get from 'lodash-es/get';
import { ExportTaskProcessor } from 'app/layout/export-overlay';
import { formatDate, formatHour, getExtraStatusName, getSourceDocument, getNextDocument, formatLuxonDate } from '../../order-list-components/exports/sales-orders-list.utils';

export class SalesQuotationListCommonExportProcessor extends ExportTaskProcessor {
  constructor(protected decimalPipe: DecimalPipe) {
    super();
  }

  protected transformForMainSection(item: OrderAttributes) {
    return {
      serial: `${get(item, 'serial', '-')}`,
      externalCode: ` ${get(item, 'externalKeys.code', '-')}`,
      status: ExportTaskProcessor.types.ave.status(get(item, 'statusId', '-')),
      statusERP: ExportTaskProcessor.types.ave.statusERP(get(item, 'statusERP')),
      trip: get(item, 'trip.id', '??'),
      route: `${get(item, 'subRoute.externalKeys.code', '??')} ${get(item, 'subRoute.name', '??')}`,
      user: `${get(item, 'user.user_login.username', '??')}`,
      //responsibleCode: `${get(item, 'responsibleBusinessPartner.personNumber', '-')} `,
      responsibleCode: `${get(item, 'responsibleBusinessPartner.externalKeys.code', '-')}`,
      responsible: `${get(item, 'responsibleBusinessPartner.name', '-')}`,
      clientCode: `${get(item, 'soldTo.externalKeys.code', '-')}`,
      client:`${get(item, 'soldTo.name', '-')}`,
      NIT: `${get(item, 'soldTo.NIT', '')}`,
      codeClass:`${get(item, 'class.code', '-')}`,
      class: `${get(item, 'class.name', '-')}`,
      destinationCode: `${get(item, 'shipTo.externalKeys.code', '??')}`,
      destination: `${get(item, 'shipTo.name', '??')}`,
      address: [
        get(item, 'address.address', ''),
        get(item, 'address.postalCode', ''),
        get(item, 'address.contactName', ''),
        get(item, 'address.locationTwo.name', ''),
        get(item, 'address.locationThree.name', ''),
        get(item, 'address.locationOne.name', ''),
      ].filter((s) => s).join(' , '),
      expeditionCondition: `${get(item, 'expeditionCondition.externalKeys.code', '')} - ${get(item, 'expeditionCondition.name', '')}`,
      paynmentConditionCod: `${get(item, 'paymentCondition.externalKeys.code', '')}`,
      paymentCondition: `${get(item, 'paymentCondition.name', '')}`,
      note: get(item, 'note', '-'),
      loadDateTime: formatDate(item.loadDateTime),
      deliveryDateTime: formatDate(item.deliveryDateTime),
      extraStatusERPGlobal: getExtraStatusName(item.extraStatusERP, 'GLOBAL'),
      extraStatusERPReject: getExtraStatusName(item.extraStatusERP, 'REJECT'),
      extraStatusERPDelivery: getExtraStatusName(item.extraStatusERP, 'DELIVERY'),
      extraStatusERPCredit: getExtraStatusName(item.extraStatusERP, 'CREDIT'),
      extraStatusERPInvoice: getExtraStatusName(item.extraStatusERP, 'INVOICE'),
      extraStatusERPUser: getExtraStatusName(item.extraStatusERP, 'USER'),
      createDateTimeDate: formatLuxonDate(get(item, 'createDateTime') || get(item, 'creationDateTime') || get(item, 'createdAt'), 'dd-MM-yyyy'),
      createDateTimeHour: formatLuxonDate(get(item, 'createDateTime') || get(item, 'creationDateTime') || get(item, 'createdAt'), 'hh:mm:ss a'),
      updateAuditAtDate: formatLuxonDate(get(item, 'updateAuditAt') || get(item, 'updatedAt') || get(item, 'updatedAuditAt'), 'dd-MM-yyyy'),
      updateAuditAtHour: formatLuxonDate(get(item, 'updateAuditAt') || get(item, 'updatedAt') || get(item, 'updatedAuditAt'), 'hh:mm:ss a'),
      updatedByUser: (() => {
        const updatedBy = get(item, 'updatedBy');
        if (!updatedBy) return '-';
        const userLogin = get(updatedBy, 'userLogin');
        if (userLogin) {
          const firstName = get(userLogin, 'first_name', '');
          const lastName = get(userLogin, 'last_name', '');
          if (firstName || lastName) {
            return `${firstName} ${lastName}`.trim();
          }
          return get(userLogin, 'username', '-');
        }
        const externalSystem = get(updatedBy, 'externalSystem.name');
        if (externalSystem) return externalSystem;
        return 'Sistema';
      })(),
      subtotal: this.decimalPipe.transform(get(item, 'subTotal', 0), '1.2-2'),
      totalSurcharges: this.decimalPipe.transform(get(item, 'totalSurcharges', 0), '1.2-2'),
      totalDiscounts: this.decimalPipe.transform(get(item, 'totalDiscounts', 0), '1.2-2'),
      totalTaxes: this.decimalPipe.transform(get(item, 'totalTaxes', 0), '1.2-2'),
      total: this.decimalPipe.transform(get(item, 'total', 0), '1.2-2'),
    };
  }

  protected transformForDetailsSection(item: OrderAttributes) {
    return get(item, 'detail', []).filter((detail) => detail.statusId !== 0).map((detail) => ({
      serial: `${get(item, 'serial', '-')} `,
      externalCode: ` ${get(item, 'externalKeys.code', '-')}`,
      sourceDocument: getSourceDocument(item),
      nextDocument: getNextDocument(item),
      trip: get(item, 'trip.id', '??'),
      materialCode: `${get(detail, 'material.externalKeys.code')} `,
      material: `${get(detail, 'material.name', '??')}`,
      note: get(detail, 'note', '-'),
      warehouse: `${get(detail, 'warehouse.externalKeys.code', '??')} - ${get(detail, 'warehouse.name', '??')}`,
      soldQuantity: `${get(detail, 'soldQuantity', '??')} (${get(detail, 'soldUnitOfMeasure.name', '??')})`,
      price: this.decimalPipe.transform(get(detail, 'unitPrice', 0), '1.2-2'),
      subtotal: this.decimalPipe.transform(get(detail, 'subTotal', 0), '1.2-2'),
      totalSurcharges: this.decimalPipe.transform(get(detail, 'totalSurcharges', 0), '1.2-2'),
      totalDiscounts: this.decimalPipe.transform(get(detail, 'totalDiscounts', 0), '1.2-2'),
      totalTaxes: this.decimalPipe.transform(get(detail, 'totalTaxes', 0), '1.2-2'),
      total: this.decimalPipe.transform(get(detail, 'total', 0), '1.2-2'),
    }));
  }
}
